<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'

const isSupported = ref(false)
const isEnabled = ref(false)
const enabledAtLeastOnce = ref(false)

let wakeLockSentinel: WakeLockSentinel | null = null

const statusText = computed(() => {
  return isEnabled.value ? 'Kochmodus ausschalten' : 'Kochmodus anschalten'
})

// Tries to acquire a screen wake lock when feature support, user intent and visibility state allow it.
async function requestWakeLock (): Promise<void> {
  if (
    !isSupported.value ||
    !isEnabled.value ||
    wakeLockSentinel ||
    document.visibilityState !== 'visible'
  ) return

  try {
    wakeLockSentinel = await navigator.wakeLock.request('screen')
    wakeLockSentinel.addEventListener('release', handleWakeLockRelease)
  } catch {
    wakeLockSentinel = null
  }
}

// Releases the currently active wake lock and resets local activity state.
async function releaseWakeLock (): Promise<void> {
  if (!wakeLockSentinel) return

  const currentWakeLock = wakeLockSentinel

  wakeLockSentinel = null
  currentWakeLock.removeEventListener('release', handleWakeLockRelease)

  if (!currentWakeLock.released) {
    await currentWakeLock.release()
  }
}

// Keeps local state in sync when the browser releases the lock externally.
function handleWakeLockRelease (): void {
  wakeLockSentinel?.removeEventListener('release', handleWakeLockRelease)
  wakeLockSentinel = null
}

// Re-acquires wake lock when tab becomes visible again and user toggle is still enabled.
async function handleVisibilityChange (): Promise<void> {
  if (document.visibilityState === 'visible' && isEnabled.value) {
    await requestWakeLock()
  }
}

// Applies a deterministic wake-lock toggle state and tracks the first activation for analytics.
function setWakeLockEnabled (nextValue: boolean): void {
  if (isEnabled.value === nextValue) return

  isEnabled.value = nextValue

  if (nextValue && !enabledAtLeastOnce.value) {
    triggerGAForWakeLock()
    enabledAtLeastOnce.value = true
  }
}

// Used by text click and keyboard handlers to flip the current state.
function handleToggleChange (): void {
  setWakeLockEnabled(!isEnabled.value)
}

// Tracks first successful user activation in analytics.
function triggerGAForWakeLock (): void {
  const dataLayerEvent = {
    event: 'wakelock_on'
  }
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push(dataLayerEvent)
}

watch(isEnabled, async enabled => {
  await (enabled ? requestWakeLock() : releaseWakeLock())
})

onMounted(() => {
  isSupported.value = 'wakeLock' in navigator

  if (isSupported.value) {
    document.addEventListener('visibilitychange', handleVisibilityChange)
  }
})

onUnmounted(() => {
  if (isSupported.value) {
    document.removeEventListener('visibilitychange', handleVisibilityChange)
    releaseWakeLock().catch(console.error)
  }
})
</script>

<template>
  <div v-if="isSupported"
       class="bx-wake-lock">
    <div class="bx-wake-lock__wrapper">
      <span id="wakeLockStatus"
            @click="handleToggleChange()">
        <strong>{{ statusText }}</strong>
        <span v-if="!isEnabled && !enabledAtLeastOnce">
          (Display bleibt an)
        </span>
      </span>
      <label class="bx-switch"
             tabindex="0"
             role="switch"
             :aria-checked="isEnabled"
             aria-labelledby="wakeLockStatus"
             @click.prevent="handleToggleChange()"
             @keyup.enter="handleToggleChange()">
        <input type="checkbox"
               :checked="isEnabled"
               tabindex="-1">
        <span />
      </label>
    </div>
  </div>
</template>
